// src/components/charts/base/TimeSeriesChart.tsx
/**
 * TimeSeriesChart Component
 * 
 * A reusable time series chart with support for:
 * - Single or multiple series
 * - Rolling metric calculations (volatility, sharpe, sortino, drawdown)
 * - Mean ± standard deviation bands
 * - Configurable time ranges and windows
 * - Consistent dark theme styling
 * 
 * @example
 * // Simple usage with raw data
 * <TimeSeriesChart
 *   dates={['2024-01-01', '2024-01-02', ...]}
 *   series={[{ name: 'Price', values: [100, 102, ...] }]}
 *   yAxisFormat="currency"
 * />
 * 
 * @example
 * // With rolling metric calculation
 * <TimeSeriesChart
 *   dates={dates}
 *   series={[{ name: 'ETH', values: prices }]}
 *   transform="volatility"
 *   window={90}
 *   yAxisFormat="percent"
 * />
 */

import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';

import {
  CHART_COLORS,
  createTooltip,
  createGrid,
  createLegend,
  createXAxis,
  createYAxis,
  smartTooltipPosition,
} from '@/utils/charts/theme';
import { hexToRgba } from '@/utils/colors';
import {
  formatCurrencyShort,
  formatPercent,
  formatRatio,
  formatDateLabel,
} from '@/utils/charts/formatters';
import { useWidgetHeight } from '@/contexts/widgetHeightContext';
import {
  toReturns,
  computeDrawdown,
  computeMeanBands,
  sliceByRange,
  parseDateToTimestamp,
  type TimeRange,
} from '@/utils/charts/dataUtils';
import { rollingSharpe, rollingSortino, rollingVolatilityPercent, TRADING_DAYS } from '@/lib/metrics';

// ============================================================================
// TYPES
// ============================================================================

export type YAxisFormat = 'currency' | 'percent' | 'ratio' | 'number';

export type Transform = 
  | 'none'
  | 'returns'
  | 'cumulative'
  | 'volatility'
  | 'sharpe'
  | 'sortino'
  | 'drawdown';

export type StackType = 'none' | 'stacked' | '100-stacked';

export interface SeriesConfig {
  name: string;
  values: (number | null)[];
  color?: string;
  dashed?: boolean;
  areaStyle?: boolean;
  /** Stack ID for grouping series */
  stackId?: string;
  /** Stack strategy for handling values */
  stackStrategy?: 'all' | 'positive' | 'negative';
  /** Use secondary Y-axis */
  yAxisIndex?: 0 | 1;
}

export interface TimeSeriesChartProps {
  /** Date labels (ISO strings, timestamps, or YYYYMMDD integers) */
  dates: (string | number)[];
  /** One or more data series */
  series: SeriesConfig[];
  /** Chart height in pixels or percentage string */
  height?: number | string;
  /** Time range to display */
  range?: TimeRange;
  /** Transform to apply to data */
  transform?: Transform;
  /** Rolling window for transform calculations */
  window?: number;
  /** Y-axis format type */
  yAxisFormat?: YAxisFormat;
  /** Custom Y-axis formatter function */
  yAxisFormatter?: (value: number) => string;
  /** Y-axis label */
  yAxisName?: string;
  /** Y-axis inverse (for ranks) */
  yAxisInverse?: boolean;
  /** Y-axis min */
  yAxisMin?: number;
  /** Y-axis max */
  yAxisMax?: number;
  /** Y-axis split number */
  yAxisSplitNumber?: number;
  /** Secondary Y-axis configuration */
  secondaryYAxis?: {
    format?: YAxisFormat;
    formatter?: (value: number) => string;
    name?: string;
    min?: number;
    max?: number;
  };
  /** Show legend */
  showLegend?: boolean;
  /** Legend position */
  legendPosition?: 'top' | 'bottom';
  /** Show mean ± std deviation bands */
  showMeanBands?: boolean;
  /** Stacking type */
  stackType?: StackType;
  /** Custom tooltip formatter */
  tooltipFormatter?: (params: unknown) => string;
  /** Additional className */
  className?: string;
}

// ============================================================================
// TRANSFORM FUNCTIONS
// ============================================================================

function applyTransform(
  values: (number | null)[],
  transform: Transform,
  window: number
): number[] {
  // Convert to numbers, replacing nulls with NaN
  const numValues = values.map(v => v === null ? NaN : v);
  
  switch (transform) {
    case 'returns':
      return toReturns(numValues);
    
    case 'cumulative': {
      const returns = toReturns(numValues);
      const cumulative: number[] = [100];
      let current = 100;
      for (const r of returns) {
        if (Number.isFinite(r)) {
          current *= (1 + r);
        }
        cumulative.push(current);
      }
      return cumulative;
    }
    
    case 'volatility': {
      const returns = toReturns(numValues);
      const minPts = window === 30 ? 20 : window === 90 ? 60 : window === 180 ? 120 : 180;
      // Use canonical lib/metrics implementation. Convert null → NaN to preserve chart trimming behavior.
      return rollingVolatilityPercent(returns, window, minPts, TRADING_DAYS.CRYPTO).map(v => v ?? NaN);
    }
    
    case 'sharpe': {
      const returns = toReturns(numValues);
      const minPts = window === 30 ? 20 : window === 90 ? 60 : window === 180 ? 120 : 180;
      return rollingSharpe(returns, window, minPts, TRADING_DAYS.CRYPTO).map(v => v ?? NaN);
    }
    
    case 'sortino': {
      const returns = toReturns(numValues);
      const minPts = window === 30 ? 20 : window === 90 ? 60 : window === 180 ? 120 : 180;
      return rollingSortino(returns, window, minPts, TRADING_DAYS.CRYPTO).map(v => v ?? NaN);
    }
    
    case 'drawdown': {
      const { values: dd } = computeDrawdown(numValues);
      return dd;
    }
    
    default:
      return numValues;
  }
}

function getYAxisFormatter(format: YAxisFormat): (v: number) => string {
  switch (format) {
    case 'currency':
      return formatCurrencyShort;
    case 'percent':
      return (v: number) => formatPercent(v, { decimals: 1 });
    case 'ratio':
      return (v: number) => formatRatio(v, 2);
    default:
      return (v: number) => v.toFixed(2);
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

/** Vertical space consumed by DataCard: p-4 padding (32px) + title row (32px) */
const DATACARD_OVERHEAD = 64;

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({
  dates,
  series,
  height = 400,
  range = 'All',
  transform = 'none',
  window = 90,
  yAxisFormat = 'number',
  yAxisFormatter: customYAxisFormatter,
  yAxisName,
  yAxisInverse = false,
  yAxisMin,
  yAxisMax,
  yAxisSplitNumber,
  secondaryYAxis,
  showLegend = true,
  legendPosition = 'top',
  showMeanBands = false,
  stackType = 'none',
  tooltipFormatter: customTooltipFormatter,
  className = '',
}) => {
  const option = useMemo(() => {
    // Apply range slicing
    const slicedDates = sliceByRange(dates, range);
    
    // Process each series
    const processedSeries = series.map((s, idx) => {
      const slicedValues = sliceByRange(s.values, range);
      const transformed = applyTransform(slicedValues, transform, window);
      
      // Align dates for return-based transforms (they lose one point)
      const isReturnBased = ['returns', 'volatility', 'sharpe', 'sortino'].includes(transform);
      const alignedDates = isReturnBased ? slicedDates.slice(1) : slicedDates;
      const alignedValues = isReturnBased ? transformed : transformed.slice(0, slicedDates.length);
      
      // Find first valid index and trim
      const firstValid = alignedValues.findIndex(v => Number.isFinite(v));
      const trimmedDates = firstValid >= 0 ? alignedDates.slice(firstValid) : alignedDates;
      const trimmedValues = firstValid >= 0 ? alignedValues.slice(firstValid) : alignedValues;
      
      return {
        name: s.name,
        dates: trimmedDates,
        values: trimmedValues,
        color: s.color || CHART_COLORS.series[idx % CHART_COLORS.series.length],
        dashed: s.dashed,
        areaStyle: s.areaStyle ?? (series.length === 1),
      };
    });
    
    // Use dates from first series
    const chartDates = processedSeries[0]?.dates || [];
    
    // Format date labels
    const dateLabels = chartDates.map(d => {
      if (typeof d === 'number') {
        // IMPORTANT: numeric dates can be YYYYMMDD or unix timestamps; normalize first
        return formatDateLabel(parseDateToTimestamp(d), 'medium');
      }
      return String(d);
    });
    
    // Handle 100% stacked normalization
    let normalizedSeries = processedSeries;
    if (stackType === '100-stacked') {
      const n = chartDates.length;
      const normalized = processedSeries.map(s => ({ ...s, values: [...s.values] }));
      for (let idx = 0; idx < n; idx++) {
        const valuesAtT = normalized.map(s => s.values[idx]).filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
        const total = valuesAtT.reduce((sum, v) => sum + v, 0);
        if (total > 0) {
          normalized.forEach(s => {
            const cur = typeof s.values[idx] === 'number' ? s.values[idx] as number : 0;
            s.values[idx] = (cur / total) * 100;
          });
        }
      }
      normalizedSeries = normalized;
    }

    // Build ECharts series
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const echartsSeries: any[] = normalizedSeries.map((s, idx) => {
      const origSeries = series[idx];
      const isStacked = stackType !== 'none';
      const areaOpacity = stackType === '100-stacked' ? 0.8 : stackType === 'stacked' ? 0.5 : 0.3;
      
      return {
        name: s.name,
        type: 'line',
        data: s.values,
        smooth: false,
        showSymbol: false,
        ...(isStacked ? { stack: 'total' } : {}),
        ...(origSeries?.yAxisIndex !== undefined ? { yAxisIndex: origSeries.yAxisIndex } : {}),
        lineStyle: { 
          width: 2, 
          color: s.color,
          type: s.dashed ? 'dashed' : 'solid',
        },
        itemStyle: { color: s.color },
        ...((s.areaStyle || isStacked) ? {
          areaStyle: { 
            opacity: areaOpacity,
            color: s.color ? hexToRgba(s.color, areaOpacity) : CHART_COLORS.gradients.blueArea,
          },
        } : {}),
      };
    });
    
    // Add mean bands if requested
    if (showMeanBands && processedSeries.length === 1) {
      const values = processedSeries[0]!.values;
      const bands = computeMeanBands(values);
      
      if (Number.isFinite(bands.mean)) {
        const bandColor = '#94a3b8';
        
        echartsSeries.push({
          name: 'μ+σ',
          type: 'line',
          data: chartDates.map(() => bands.upper),
          showSymbol: false,
          lineStyle: { width: 1, type: 'dashed', color: bandColor },
          itemStyle: { color: bandColor },
        });
        
        echartsSeries.push({
          name: 'μ-σ',
          type: 'line',
          data: chartDates.map(() => bands.lower),
          showSymbol: false,
          lineStyle: { width: 1, type: 'dashed', color: bandColor },
          itemStyle: { color: bandColor },
        });
      }
    }
    
    // Calculate Y axis bounds
    const allValues = processedSeries.flatMap(s => s.values).filter(Number.isFinite);
    const hasValues = allValues.length > 0;
    const minVal = hasValues ? Math.min(...allValues) : 0;
    const maxVal = hasValues ? Math.max(...allValues) : 100;
    const dataRange = maxVal - minVal;
    const pad = hasValues ? dataRange * 0.1 : 0;
    
    // Smart rounding based on data magnitude to preserve precision for small values
    const roundToNiceBounds = (val: number, direction: 'floor' | 'ceil'): number => {
      if (!Number.isFinite(val) || val === 0) return val;
      const absVal = Math.abs(val);
      // Find appropriate decimal places based on data range
      const magnitude = dataRange > 0 ? Math.floor(Math.log10(dataRange)) : Math.floor(Math.log10(absVal));
      const roundFactor = Math.pow(10, -magnitude + 1);
      if (direction === 'floor') {
        return Math.floor(val * roundFactor) / roundFactor;
      }
      return Math.ceil(val * roundFactor) / roundFactor;
    };
    
    // Resolve formatter
    const yFormatter = customYAxisFormatter || getYAxisFormatter(yAxisFormat);
    
    // Default tooltip formatter
    const defaultTooltipFormatter = (params: unknown) => {
      const items = params as Array<{ axisValue: string; seriesName: string; value: number; marker: string }>;
      if (!items || items.length === 0) return '';

      const dateStr = items[0]?.axisValue || '';
      let html = `<div style="font-size:11px;font-weight:500;margin-bottom:4px;color:#9ca3af;">${dateStr}</div>`;

      // Sort items by value in descending order
      const sortedItems = [...items].sort((a, b) => Number(b.value) - Number(a.value));

      sortedItems.forEach(item => {
        const val = Number(item.value);
        const color = yAxisFormat === 'percent' || yAxisFormat === 'ratio'
          ? (val >= 0 ? '#22c55e' : '#ef4444')
          : '#f3f4f6';
        html += `${item.marker} ${item.seriesName}: <span style="color:${color}">${yFormatter(val)}</span><br/>`;
      });

      return html;
    };
    
    // Use provided bounds or calculated bounds
    const effectiveMin = yAxisMin !== undefined ? yAxisMin : (hasValues ? roundToNiceBounds(minVal - pad, 'floor') : undefined);
    const effectiveMax = yAxisMax !== undefined ? yAxisMax : (hasValues ? roundToNiceBounds(maxVal + pad, 'ceil') : undefined);

    return {
      backgroundColor: CHART_COLORS.background,
      tooltip: {
        ...createTooltip(),
        position: smartTooltipPosition,
        formatter: customTooltipFormatter || defaultTooltipFormatter,
      },
      legend: showLegend ? {
        ...createLegend(),
        ...(legendPosition === 'bottom' ? { bottom: 0, top: 'auto' } : {}),
      } : { show: false },
      grid: createGrid(),
      xAxis: {
        ...createXAxis({ data: dateLabels, boundaryGap: false }),
        axisLabel: {
          color: CHART_COLORS.axis.label,
          margin: 14,
          showMaxLabel: true,
        },
      },
      yAxis: secondaryYAxis ? [
        createYAxis({
          formatter: yFormatter,
          ...(yAxisName ? { name: yAxisName } : {}),
          ...(effectiveMin !== undefined ? { min: effectiveMin } : {}),
          ...(effectiveMax !== undefined ? { max: effectiveMax } : {}),
          ...(yAxisInverse ? { inverse: true } : {}),
          ...(yAxisSplitNumber !== undefined ? { splitNumber: yAxisSplitNumber } : {}),
        }),
        createYAxis({
          formatter: secondaryYAxis.formatter || getYAxisFormatter(secondaryYAxis.format || 'number'),
          ...(secondaryYAxis.name ? { name: secondaryYAxis.name } : {}),
          ...(secondaryYAxis.min !== undefined ? { min: secondaryYAxis.min } : {}),
          ...(secondaryYAxis.max !== undefined ? { max: secondaryYAxis.max } : {}),
        }),
      ] : createYAxis({
        formatter: yFormatter,
        ...(yAxisName ? { name: yAxisName } : {}),
        ...(effectiveMin !== undefined ? { min: effectiveMin } : {}),
        ...(effectiveMax !== undefined ? { max: effectiveMax } : {}),
        ...(yAxisInverse ? { inverse: true } : {}),
        ...(yAxisSplitNumber !== undefined ? { splitNumber: yAxisSplitNumber } : {}),
      }),
      series: echartsSeries,
    } as EChartsOption;
  }, [dates, series, range, transform, window, yAxisFormat, customYAxisFormatter, yAxisName, yAxisInverse, yAxisMin, yAxisMax, yAxisSplitNumber, secondaryYAxis, showLegend, legendPosition, showMeanBands, stackType, customTooltipFormatter]);
  
  // When rendered inside an Operation Center widget, fill the available height
  // rather than using the card's fixed pixel height.
  const widgetContentHeight = useWidgetHeight();
  const effectiveHeight =
    widgetContentHeight !== null
      ? Math.max(80, widgetContentHeight - DATACARD_OVERHEAD)
      : height;

  return (
    <div className={className} style={typeof effectiveHeight === 'string' ? { height: '100%' } : undefined}>
      <ReactECharts
        option={option}
        style={{ height: effectiveHeight, width: '100%' }}
        opts={{ renderer: 'canvas' }}
        notMerge
        lazyUpdate
      />
    </div>
  );
};

export default TimeSeriesChart;

